import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Mail, AlertTriangle, ChevronDown, ChevronUp, RefreshCw } from "lucide-react";

/**
 * Compact KPI tile on the dispatcher board: sent vs failed customer emails
 * over the last 7 days. Turns red when Resend delivery is failing (bad
 * domain, daily quota exhausted, etc.) so dispatch can act immediately.
 */
export default function EmailHealthTile() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const reload = async () => {
    setBusy(true);
    try {
      const r = await api.get("/admin/email-health");
      setData(r.data);
    } catch (_e) {
      // Silently fail — tile just shows nothing rather than crashing the board
    } finally { setBusy(false); }
  };

  useEffect(() => { reload(); }, []);

  if (!data) return null;

  const healthy = data.failed === 0;
  const fmtWhen = (iso) => (iso ? new Date(iso).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—");

  if (healthy) {
    return (
      <div data-testid="email-health-tile" className="mb-6 border border-emerald-500/20 bg-emerald-500/10 px-5 py-3 flex items-center gap-3 text-[12px]">
        <Mail size={14} className="text-emerald-300"/>
        <span className="text-emerald-300 uppercase tracking-[0.18em] text-[10px]" data-testid="email-health-summary">
          Email delivery healthy · {data.sent} sent last {data.window_days} days
        </span>
        <button data-testid="email-health-refresh" onClick={reload} disabled={busy}
          className="ml-auto text-emerald-300 hover:opacity-70" title="Refresh">
          <RefreshCw size={12} className={busy ? "animate-spin" : ""}/>
        </button>
      </div>
    );
  }

  return (
    <div data-testid="email-health-tile" className="mb-6 border border-red-500/30 bg-gradient-to-r from-red-500/10 via-red-500/5 to-transparent">
      <div className="px-5 py-4 flex items-center gap-5 flex-wrap">
        <div className="flex items-center gap-2 shrink-0">
          <Mail size={16} className="text-red-300"/>
          <span className="text-[10px] uppercase tracking-[0.22em] text-[#A1A1AA]">Email health · {data.window_days}d</span>
        </div>

        <div className="flex items-baseline gap-3">
          <span className="font-serif text-2xl text-emerald-300" data-testid="email-health-sent">{data.sent}</span>
          <span className="text-[10px] uppercase tracking-[0.18em] text-[#71717A]">sent</span>
          <span className="font-serif text-2xl text-red-300" data-testid="email-health-failed">{data.failed}</span>
          <span className="text-[10px] uppercase tracking-[0.18em] text-[#71717A]">failed</span>
        </div>

        {data.quota_exhausted && (
          <div data-testid="email-health-quota-banner" className="flex items-center gap-2 border border-red-500/40 bg-red-500/10 px-3 py-1.5 text-[11px] text-red-300">
            <AlertTriangle size={12}/>
            Resend daily quota reached — emails will fail until quota resets (or upgrade plan)
          </div>
        )}

        <div className="text-[11px] text-[#A1A1AA]">
          last failure <span className="text-[#F5F2EB]">{fmtWhen(data.last_failed_at)}</span>
        </div>

        <div className="ml-auto flex items-center gap-2">
          <button data-testid="email-health-refresh" onClick={reload} disabled={busy}
            className="btn-ghost !py-1.5 !px-3 text-[10px]">
            <RefreshCw size={12} className={busy ? "animate-spin" : ""}/>Refresh
          </button>
          <button data-testid="email-health-toggle" onClick={() => setExpanded(v => !v)}
            className="btn-ghost !py-1.5 !px-3 text-[10px]">
            {expanded ? <><ChevronUp size={12}/>Hide</> : <><ChevronDown size={12}/>View failures</>}
          </button>
        </div>
      </div>

      {expanded && (
        <div data-testid="email-health-failures" className="border-t border-white/10 max-h-64 overflow-y-auto">
          <table className="w-full text-[11px]">
            <thead className="bg-black/40 text-[9px] uppercase tracking-[0.18em] text-[#71717A] sticky top-0">
              <tr>
                <th className="text-left px-4 py-2">When</th>
                <th className="text-left px-2">To</th>
                <th className="text-left px-2">Subject</th>
                <th className="text-left px-4">Error</th>
              </tr>
            </thead>
            <tbody>
              {data.recent_failures.map((f, i) => (
                <tr key={i} className="border-t border-white/5" data-testid={`email-failure-row-${i}`}>
                  <td className="px-4 py-2 text-[#A1A1AA] whitespace-nowrap">{fmtWhen(f.created_at)}</td>
                  <td className="px-2 text-[#F5F2EB] truncate max-w-[180px]">{f.to}</td>
                  <td className="px-2 text-[#A1A1AA] truncate max-w-[240px]">{f.subject || f.tag}</td>
                  <td className="px-4 font-mono text-red-300">{f.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
